import { useAppSelector, useAppDispatch } from '../../app/hooks';
import {
  processMaintenanceInfo,
  useMaintenanceInfo,
} from '@features/admin/adminAPI';
import {
  selectShowMaintenanceBanner,
  selectLeoCloudUnderMaintenance,
  selectTerminalModeUnderMaintenance,
  setShowMaintenanceBanner,
  setLeoCloudUnderMaintenance,
  setTerminalModeUnderMaintenance,
} from '@features/view/viewSlice';
import { useInterval } from 'ahooks';
import { isEqual } from 'lodash';
import { useState } from 'react';

export default function useMaintenance() {
  const dispatch = useAppDispatch();

  const leoCloudUnderMaintenance = useAppSelector(
    selectLeoCloudUnderMaintenance,
  );
  const terminalModeUnderMaintenance = useAppSelector(
    selectTerminalModeUnderMaintenance,
  );
  const showMaintenanceBanner = useAppSelector(selectShowMaintenanceBanner);

  const [intervalDelay, setIntervalDelay] = useState<number | undefined>(
    undefined,
  );

  // I am planning on retrieving this on a 90 second interval
  const { data } = useMaintenanceInfo();
  const [prevData, setPrevData] = useState(data);

  if (!isEqual(data, prevData)) {
    // immediatly check the details for new maintenance
    setIntervalDelay(0);
    setPrevData(data);
  }
  const checkStatus = () => {
    if (!data) return;
    const info = processMaintenanceInfo(data);
    if (!info) {
      // reset everything
      if (showMaintenanceBanner) dispatch(setShowMaintenanceBanner(false));
      if (leoCloudUnderMaintenance)
        dispatch(setLeoCloudUnderMaintenance(false));
      if (terminalModeUnderMaintenance)
        dispatch(setTerminalModeUnderMaintenance(false));
      setIntervalDelay(90000);
      return;
    }
    const {
      isWarningActive,
      isMaintenanceActive,
      isLeoCloudMaintenance,
      isTerminalModeMaintenance,
      timeUntilWarningStart,
      timeUntilMaintenanceStart,
      timeUntilEnd,
    } = info;

    // Handle Maintenance Banner
    if (isWarningActive && !showMaintenanceBanner) {
      dispatch(setShowMaintenanceBanner(true));
    } else if (!isWarningActive && showMaintenanceBanner) {
      dispatch(setShowMaintenanceBanner(false));
    }

    // Handle Leo Cloud Maintenance
    if (
      isLeoCloudMaintenance &&
      isMaintenanceActive &&
      !leoCloudUnderMaintenance
    ) {
      dispatch(setLeoCloudUnderMaintenance(true));
    } else if (!isMaintenanceActive && leoCloudUnderMaintenance) {
      dispatch(setLeoCloudUnderMaintenance(false));
    }

    // Handle Terminal Mode Maintenance
    if (
      isTerminalModeMaintenance &&
      isMaintenanceActive &&
      !terminalModeUnderMaintenance
    ) {
      dispatch(setTerminalModeUnderMaintenance(true));
    } else if (!isMaintenanceActive && terminalModeUnderMaintenance) {
      dispatch(setTerminalModeUnderMaintenance(false));
    }

    if (
      timeUntilWarningStart.seconds !== undefined &&
      timeUntilWarningStart.seconds >= 0 &&
      timeUntilWarningStart.seconds < 3600
    ) {
      console.info({
        settingTimeOutDelayForWarning: Math.round(
          timeUntilWarningStart.seconds * 1000,
        ),
      });
      setIntervalDelay(Math.round(timeUntilWarningStart.seconds * 1000));
    } else if (
      timeUntilMaintenanceStart.seconds !== undefined &&
      timeUntilMaintenanceStart.seconds >= 0 &&
      timeUntilMaintenanceStart.seconds < 3600
    ) {
      console.info({
        settingTimeOutDelayForMaintenance: Math.round(
          timeUntilMaintenanceStart.seconds * 1000,
        ),
      });
      setIntervalDelay(Math.round(timeUntilMaintenanceStart.seconds * 1000));
    } else if (
      timeUntilEnd.seconds !== undefined &&
      timeUntilEnd.seconds >= 0 &&
      timeUntilEnd.seconds < 3600
    ) {
      setIntervalDelay(Math.round(timeUntilEnd.seconds * 1000));
    } else {
      setIntervalDelay(90000);
    }
  };

  // I think this should be a interval
  useInterval(checkStatus, intervalDelay);
}
